import PropTypes from 'prop-types';
import React, { Component } from 'react';
import onClickOutside from 'react-onclickoutside';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from 'react-intl';
import styled from 'styled-components';
import { Focus } from '@fenderdigital/ui/utils/css';
import { SearchSelectionProvider } from '../../contexts/SearchSelectionContext';
import SearchSuggestions from './SearchSuggestions';
import SearchPanelResults from './SearchPanelResults';
import SearchPanelScreenReaderInstructions from './SearchPanelScreenReaderInstructions';
import SearchField from '../SearchField/SearchFieldContainer';
import messages from './messages';

const SearchPanelContainer = styled.div`
  ${Focus};
  position: absolute;
  left: 0;
  z-index: 3;
  padding-bottom: 1rem;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-top-width: 0;
  display: ${({ isPanelVisible }) => (isPanelVisible ? 'block' : 'none')};
  top: 100%;
  max-width: 24rem;
  overflow: hidden;
`;

export class SearchPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isPanelVisible: false,
      instrument: 'guitar',
      results: {
        hits: [],
      },
    };
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleInstrument = this.handleInstrument.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.setLocalResults = this.setLocalResults.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    this.unlisten = this.props.history.listen(() => {
      const { onCloseClick, pill } = this.props;
      if (onCloseClick) onCloseClick();
      if (pill) this.handleClose();
    });
  }

  componentWillUnmount() {
    this.unlisten();
  }

  handleClickOutside(event) {
    event.stopPropagation();
    const { onCloseClick } = this.props;
    this.handleClose();
    if (onCloseClick) onCloseClick();
  }

  handleFocus() {
    this.setState({ isPanelVisible: true });
  }

  handleInstrument(instrument) {
    this.setState({ instrument });
  }

  setLocalResults(results) {
    this.setState({ results });
  }

  handleSubmit() {
    this.handleClose();
  }

  handleClose() {
    this.setState({ isPanelVisible: false });
  }

  renderLayout() {
    const { onCloseClick } = this.props;
    const { results: localResults, instrument } = this.state;
    const { hits, query } = localResults;
    return (
      <div>
        <div className="relative pa3 bg-near-white">
          <button
            type="button"
            className="outline-0 pa0 ma0 bn absolute top-1 right-1 bg-none pointer"
            data-id="search-panel-close"
            onClick={onCloseClick}
          >
            <span className="f4 icon-x mid-gray" />
          </button>
          <h2 className="pa0 mt0 mb2 f3" data-id="search-panel-title">
            Search
          </h2>
          <SearchField
            instrument={instrument}
            localResults={localResults}
            setLocalResults={this.setLocalResults}
            onSubmit={this.handleSubmit}
            hasLocalSearch
            onMountFocus
            instant
            small
          />
        </div>
        {query ? (
          <SearchPanelResults
            {...this.props}
            instrument={instrument}
            handleInstrument={this.handleInstrument}
            localResults={localResults}
            setLocalResults={this.setLocalResults}
            closeSearchPanel={onCloseClick}
          />
        ) : (
          <SearchSuggestions />
        )}
      </div>
    );
  }

  renderPillLayout() {
    const { intl, pill } = this.props;
    const { results: localResults, instrument } = this.state;
    const { hits, query } = localResults;
    const { isPanelVisible } = this.state;
    const isVisible = isPanelVisible && !!query;
    const { formatMessage } = intl;

    return (
      <div>
        <SearchField
          onFocus={this.handleFocus}
          instrument={instrument}
          localResults={localResults}
          setLocalResults={this.setLocalResults}
          onSubmit={this.handleSubmit}
          pill={pill}
          hasLocalSearch
          instant
        />
        {isVisible ? (
          <SearchPanelContainer
            isPanelVisible={isPanelVisible}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            useInsetFocus
          >
            <SearchPanelResults
              {...this.props}
              instrument={instrument}
              handleInstrument={this.handleInstrument}
              localResults={localResults}
              setLocalResults={this.setLocalResults}
              pill={pill}
              closeSearchPanel={this.handleClose}
            />
          </SearchPanelContainer>
        ) : null}
        {pill && (
          <SearchPanelScreenReaderInstructions>
            {formatMessage(messages.instructions)}
          </SearchPanelScreenReaderInstructions>
        )}
      </div>
    );
  }

  render() {
    const { pill } = this.props;
    const styles = `${!pill ? 'relative z-3 shadow-3 bg-white ba b--moon-gray futura-heavy' : ''}`;
    return (
      <SearchSelectionProvider>
        <div className={`search-panel ${styles}`} style={{ width: '24rem' }}>
          {pill ? this.renderPillLayout() : this.renderLayout()}
        </div>
      </SearchSelectionProvider>
    );
  }
}

SearchPanel.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.shape({})),
  intl: intlShape.isRequired,
  onCloseClick: PropTypes.func,
  pill: PropTypes.bool,
  query: PropTypes.string,
};

SearchPanel.defaultProps = {
  hits: null,
  onCloseClick: () => {},
  pill: false,
  query: '',
};

export default withRouter(injectIntl(onClickOutside(SearchPanel)));
